// Celebración al desbloquear una insignia de racha: burst de partículas (mismo patrón que
// SparkleOverlay, usado al enviar un reconocimiento) + la insignia ganada + felicitación. Se queda
// visible hasta que el usuario cierra el modal — a diferencia de SparkleOverlay, que se auto-destruye.
import { useMemo, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { Modal } from '@/components/common/Modal';
import { getBadgeName } from '@/api/recognition';
import type { BadgeDto } from '@/api/recognition';
import { BadgeIcon, type BadgeSkin } from './badgeSkins';

const SPARKLE_COUNT = 20;
const SPARKLE_COLORS = ['#F08521', '#FFD700', '#ffffff', '#FFE566', '#342F82', '#20B15A'];

interface SparkParticle {
  id: number;
  angle: number;
  dist: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
  shape: 'star' | 'circle';
}

interface BadgeCelebrationProps {
  badge: BadgeDto;
  skin: BadgeSkin;
  onClose: () => void;
}

export function BadgeCelebration({ badge, skin, onClose }: Readonly<BadgeCelebrationProps>) {
  const { t, i18n } = useTranslation('recognition');
  const name = getBadgeName(badge, i18n.language);
  const prefersReducedMotion = useReducedMotion();

  const sparks = useMemo<SparkParticle[]>(() => Array.from({ length: SPARKLE_COUNT }, (_, idx) => ({
    id: idx,
    angle: (idx / SPARKLE_COUNT) * 360 + (Math.random() - 0.5) * 20,
    dist: 90 + Math.random() * 140,
    size: 5 + Math.random() * 8,
    color: SPARKLE_COLORS[idx % SPARKLE_COLORS.length],
    delay: Math.random() * 0.3,
    duration: 0.9 + Math.random() * 0.3,
    shape: idx % 4 === 0 ? 'star' : 'circle',
  })), []);

  // `createPortal` a `document.body`: `StreakWidget` (dueño de esta celebración) se monta dentro de
  // una tarjeta de vidrio `.shine-card` (la Home de Brillamos, `BrillamosView.tsx`), y esas tarjetas
  // llevan `backdrop-filter` — que, por especificación CSS, convierte a ese ancestro en el
  // "containing block" de cualquier descendiente `position: fixed`. Sin portal, el
  // `.modal-overlay` de `Modal` (`inset: 0`) quedaría encajado en el tamaño de la TARJETA en
  // vez de cubrir la pantalla completa. Mismo defecto, mismo arreglo que ya se aplicó en
  // `PostItDetailModal.tsx` (ver su comentario) para el mismo tipo de tarjeta contenedora.
  return createPortal((
    <Modal
      open
      onClose={onClose}
      className="badge-celebration-box"
      labelledBy="badge-celebration-title"
    >
      <div className="badge-celebration">
        <div className="badge-celebration-sparkles" aria-hidden="true">
          {sparks.map((spark) => {
            const radians = spark.angle * (Math.PI / 180);
            return (
              <motion.div
                key={spark.id}
                className={`sparkle-p ${spark.shape === 'star' ? 'sparkle-p--star' : ''}`}
                style={{ width: spark.size, height: spark.size, background: spark.color, left: '50%', top: '50%' }}
                initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                animate={{
                  x: Math.cos(radians) * spark.dist,
                  y: Math.sin(radians) * spark.dist,
                  scale: [0, 1.2, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: spark.duration, delay: spark.delay, ease: 'easeOut',
                  repeat: prefersReducedMotion ? 0 : Infinity, repeatDelay: 0.5,
                }}
              />
            );
          })}
        </div>

        <div className="badge-celebration-icon" style={{ '--bg1': skin.g1, '--bg2': skin.g2 } as CSSProperties}>
          <BadgeIcon skin={skin} earned iconUrl={badge.iconUrl} />
        </div>

        <p id="badge-celebration-title" className="badge-celebration-title">
          {t('streak.celebration.title')}
        </p>
        <p className="badge-celebration-badge-name">{name}</p>
        <p className="badge-celebration-message">{t('streak.celebration.message')}</p>

        <button type="button" className="btn btn-primary badge-celebration-cta" onClick={onClose}>
          {t('streak.celebration.cta')}
        </button>
      </div>
    </Modal>
  ), document.body);
}

export default BadgeCelebration;
